import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { profileService, GetProfileResponse, CreateProfileRequest } from '@/services/profile-service';
import { UserProfile } from '@/types/profile';
import { getConsistentUserId, cleanupProfileCompletionKeys, cleanupAllUserKeys } from '@/utils/user-id-helpers';
import { useAuth } from '@/components/auth/AuthProvider';
import { authService } from '@/services/auth-service';

interface ProfileContextType {
  profile: GetProfileResponse['data'] | null;
  profileStrength: number;
  isLoading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  shouldShowCompletionDialog: boolean;
  setShouldShowCompletionDialog: (show: boolean) => void;
  dismissCompletionDialog: () => void;
  refreshProfile: () => Promise<void>;
  updateProfile: (profile: UserProfile) => Promise<void>;
  triggerProfileRefresh: () => void; // New method to trigger profile refresh event
  shouldShowUserTypeDialog: boolean;
  setShouldShowUserTypeDialog: (show: boolean) => void;
  updateProfileType: (userType: 'student' | 'teacher', classGrade?: string) => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

interface ProfileProviderProps {
  children: ReactNode;
}

export function ProfileProvider({ children }: ProfileProviderProps) {
  const { user } = useAuth(); // Only need user data, not profile
  const [profile, setProfile] = useState<GetProfileResponse['data'] | null>(null);
  const [profileStrength, setProfileStrength] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldShowCompletionDialog, setShouldShowCompletionDialog] = useState(false);
  const [shouldShowUserTypeDialog, setShouldShowUserTypeDialog] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Add ref to track if fetch is in progress to prevent concurrent calls
  const fetchInProgressRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  // Load profile from sessionStorage on component mount
  const loadProfileFromStorage = () => {
    try {
      const storedProfile = sessionStorage.getItem('profile_data');
      const storedStrength = sessionStorage.getItem('profile_strength');
      const storedTimestamp = sessionStorage.getItem('profile_cache_timestamp');
      
      if (storedProfile && storedStrength) {
        // Check cache freshness (optional - 1 hour cache expiry)
        if (storedTimestamp) {
          const cacheTime = new Date(storedTimestamp).getTime();
          const now = new Date().getTime();
          const cacheAge = now - cacheTime;
          const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
          
          // If cache is older than 1 hour, consider it stale but still use it to avoid API calls
          // We can implement a background refresh later if needed
        }
        
        const profileData = JSON.parse(storedProfile);
        setProfile(profileData);
        setProfileStrength(parseInt(storedStrength));
        
        // Check if dialog should be shown based on stored data
        const userId = getConsistentUserId(user, profileData);
        const dismissedKey = userId ? `profile_completion_dismissed_${userId}` : null;
        const hasDismissed = dismissedKey ? localStorage.getItem(dismissedKey) === 'true' : false;
        const shouldShow = parseInt(storedStrength) < 70 && !hasDismissed;
        
        setShouldShowCompletionDialog(shouldShow);
        
        return true;
      }
    } catch (err) {
      // Clear corrupted data
      sessionStorage.removeItem('profile_data');
      sessionStorage.removeItem('profile_strength');
      sessionStorage.removeItem('profile_cache_timestamp');
    }
    return false;
  };

  // Store profile in sessionStorage
  const storeProfileInStorage = (profileData: GetProfileResponse['data']) => {
    try {
      sessionStorage.setItem('profile_data', JSON.stringify(profileData));
      sessionStorage.setItem('profile_strength', Math.floor(profileData.profile_strength).toString());
      sessionStorage.setItem('profile_cache_timestamp', new Date().toISOString());
    } catch (err) {
      // Ignore storage errors
    }
  };

  const fetchProfile = async () => {
    // Prevent concurrent fetch calls
    if (fetchInProgressRef.current) {
      return;
    }
    
    try {
      fetchInProgressRef.current = true;
      setIsLoading(true);
      setError(null);
      
      // Get access token from auth service
      const accessToken = authService.getAccessToken();
      
      if (!accessToken) {
        throw new Error('No valid session found');
      }

      // Fetch profile data directly from profile service
      const response = await profileService.getProfile(accessToken);
      
      if (response.success && response.data) {
        setProfile(response.data);
        setProfileStrength(Math.floor(response.data.profile_strength));
        
        // Store profile in sessionStorage
        storeProfileInStorage(response.data);
        
        // Check if profile type is missing (for social login users)
        if (!response.data.profile_type) {
          setShouldShowUserTypeDialog(true);
          return; // Don't show completion dialog yet, wait for user type selection
        }
        
        // Update dialog visibility based on profile strength
        const userId = getConsistentUserId(user, response.data);
        const dismissedKey = userId ? `profile_completion_dismissed_${userId}` : null;
        const hasDismissed = dismissedKey ? localStorage.getItem(dismissedKey) === 'true' : false;
        const shouldShow = Math.floor(response.data.profile_strength) < 70 && !hasDismissed;
        
        setShouldShowCompletionDialog(shouldShow);
        
      } else {
        throw new Error(response.message || 'Failed to fetch profile');
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to fetch profile');
      
      // If profile doesn't exist, user needs to complete profile
      if (err.message?.includes('not found') || err.message?.includes('Profile not found')) {
        setShouldShowCompletionDialog(true);
        setProfileStrength(0);
        sessionStorage.removeItem('profile_data');
        sessionStorage.removeItem('profile_strength');
        sessionStorage.removeItem('profile_cache_timestamp');
      }
    } finally {
      setIsLoading(false);
      fetchInProgressRef.current = false;
    }
  };

  const refreshProfile = async () => {
    // Force refresh from API
    await fetchProfile();
  };

  const updateProfile = async (updatedProfile: UserProfile) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get access token from auth service
      const accessToken = authService.getAccessToken();
      
      if (!accessToken) {
        throw new Error('No valid session found');
      }

      // Convert UserProfile to CreateProfileRequest format
      const updateData: Partial<CreateProfileRequest> = {
        first_name: updatedProfile.first_name,
        last_name: updatedProfile.last_name,
        phone: updatedProfile.phone,
        date_of_birth: updatedProfile.date_of_birth,
        avatar_url: updatedProfile.avatar_url,
        bio: updatedProfile.bio,
        location: updatedProfile.location,
        student_details: updatedProfile.student_details,
        teacher_details: updatedProfile.teacher_details,
      };

      const response = await profileService.updateProfile(updateData, accessToken);
      
      if (response.success && response.data) {
        // Update local state with the response data
        setProfile(response.data);
        setProfileStrength(Math.floor(response.data.profile_strength));
        
        // Update sessionStorage with the new data
        storeProfileInStorage(response.data);
        
        // Update dialog visibility based on new profile strength
        const userId = getConsistentUserId(user, response.data);
        const dismissedKey = userId ? `profile_completion_dismissed_${userId}` : null;
        const hasDismissed = dismissedKey ? localStorage.getItem(dismissedKey) === 'true' : false;
        
        // Clear dismissal flag if profile is now complete (>=70%)
        if (Math.floor(response.data.profile_strength) >= 70 && hasDismissed && dismissedKey) {
          localStorage.removeItem(dismissedKey);
        }
        
        const shouldShow = Math.floor(response.data.profile_strength) < 70 && !hasDismissed;
        setShouldShowCompletionDialog(shouldShow);
        
        // Trigger profile refresh event to notify other components
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('profile-updated', {
            detail: { profile: response.data }
          }));
        }, 100);
        
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
      throw err; // Re-throw to allow form to handle error
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfileType = async (userType: 'student' | 'teacher', classGrade?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get access token from auth service
      const accessToken = authService.getAccessToken();
      
      if (!accessToken) {
        throw new Error('No valid session found');
      }

      console.log('Current profile before update:', profile);
      console.log('Updating profile type with:', { userType, classGrade });

      // Prepare update data with user type and class grade if student
      const updateData: Partial<CreateProfileRequest> = {
        profile_type: userType,
      };

      // Add student details if user is a student and class grade is provided
      if (userType === 'student' && classGrade) {
        // Preserve existing student_details and add class_grade
        updateData.student_details = {
          ...profile?.student_details, // Preserve existing student details
          class_grade: classGrade,
        };
      }

      console.log('Update data being sent:', JSON.stringify(updateData, null, 2));

      const response = await profileService.updateProfile(updateData, accessToken);
      
      console.log('Response received:', JSON.stringify(response, null, 2));
      
      if (response.success && response.data) {
        console.log('Updated profile data:', response.data);
        console.log('Profile type in response:', response.data.profile_type);
        console.log('Student details in response:', response.data.student_details);
        
        // Update local state with the response data
        setProfile(response.data);
        setProfileStrength(Math.floor(response.data.profile_strength));
        
        // Update sessionStorage with the new data
        storeProfileInStorage(response.data);
        
        // Hide the user type dialog
        setShouldShowUserTypeDialog(false);
        
        // Update completion dialog visibility based on new profile strength
        const userId = getConsistentUserId(user, response.data);
        const dismissedKey = userId ? `profile_completion_dismissed_${userId}` : null;
        const hasDismissed = dismissedKey ? localStorage.getItem(dismissedKey) === 'true' : false;
        const shouldShow = Math.floor(response.data.profile_strength) < 70 && !hasDismissed;
        
        setShouldShowCompletionDialog(shouldShow);
        
        // Trigger profile refresh event to notify other components
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('profile-updated', {
            detail: { profile: response.data }
          }));
        }, 100);
        
      } else {
        throw new Error(response.message || 'Failed to update profile type');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile type');
      throw err; // Re-throw to allow form to handle error
    } finally {
      setIsLoading(false);
    }
  };

  const dismissCompletionDialog = () => {
    const userId = getConsistentUserId(user, profile);
    if (userId) {
      const dismissedKey = `profile_completion_dismissed_${userId}`;
      localStorage.setItem(dismissedKey, 'true');
      
      // Clean up old profile completion keys for previous users/sessions
      cleanupProfileCompletionKeys(userId);
    }
    setShouldShowCompletionDialog(false);
  };

  // Initialize profile on mount and handle auth state changes
  useEffect(() => {
    let mounted = true;
    
    const initializeProfile = async () => {
      try {
        // Check if user is authenticated using auth service
        const isAuthenticated = authService.isAuthenticated();
        
        if (isAuthenticated) {
          // First, try to load from sessionStorage
          const loadedFromStorage = loadProfileFromStorage();
          
          if (!loadedFromStorage) {
            // Only fetch from API if no cached data
            if (mounted) {
              await fetchProfile();
            }
          }
        } else {
          // Clear any stored data if not authenticated
          sessionStorage.removeItem('profile_data');
          sessionStorage.removeItem('profile_strength');
          sessionStorage.removeItem('profile_cache_timestamp');
        }
        
        if (mounted) {
          setHasInitialized(true);
        }
      } catch (error) {
        if (mounted) {
          setHasInitialized(true); // Set to true even on error to prevent retries
        }
      }
    };

    // Only initialize if not already initialized
    if (!hasInitialized) {
      initializeProfile();
    }
    
    return () => {
      mounted = false;
    };
  }, [hasInitialized]); // Depend on hasInitialized to prevent multiple calls

  // Handle auth state changes separately with proper cleanup
  useEffect(() => {
    let authStateTimeout: NodeJS.Timeout | null = null;
    
    const currentUserId = user?.id || null;
    
    // Only trigger if user ID actually changed AND we're not currently fetching
    if (lastUserIdRef.current === currentUserId || fetchInProgressRef.current) {
      return;
    }
    
    lastUserIdRef.current = currentUserId;
    
    // Since we're using the auth service, we'll monitor the user state from useAuth
    const handleAuthStateChange = async () => {
      // Clear any pending processing
      if (authStateTimeout) {
        clearTimeout(authStateTimeout);
        authStateTimeout = null;
      }
      
      // Debounce to prevent rapid-fire calls
      authStateTimeout = setTimeout(async () => {
        try {
          if (user && currentUserId) {
            // Clean up any old user data
            cleanupAllUserKeys(currentUserId);
            
            // Only fetch if we haven't already fetched for this user and not currently fetching
            if (!profile || profile.user_id !== currentUserId) {
              if (!fetchInProgressRef.current) {
                await fetchProfile();
              }
            }
            
          } else {
            // User is signed out
            setProfile(null);
            setProfileStrength(0);
            setShouldShowCompletionDialog(false);
            setError(null);
            setHasInitialized(false);
            // Clear storage
            sessionStorage.removeItem('profile_data');
            sessionStorage.removeItem('profile_strength');
            sessionStorage.removeItem('profile_cache_timestamp');
          }
        } catch (error) {
          // Handle auth state change errors silently
        }
        authStateTimeout = null;
      }, 300); // Increased debounce time
    };

    // Call the handler when user state changes
    handleAuthStateChange();

    return () => {
      if (authStateTimeout) {
        clearTimeout(authStateTimeout);
      }
    };
  }, [user?.id]) // Only depend on user ID, not the entire user object

  // Listen for login success events to immediately fetch profile
  useEffect(() => {
    const handleLoginSuccess = async (e: Event) => {
      const customEvent = e as CustomEvent;
      const { user: loginUser } = customEvent.detail;
      if (loginUser && !fetchInProgressRef.current) {
        // Force fetch profile after successful login
        await fetchProfile();
      }
    };

    const handleProfileUpdate = async (e: Event) => {
      const customEvent = e as CustomEvent;
      const { profile: updatedProfile } = customEvent.detail;
      if (updatedProfile && !fetchInProgressRef.current) {
        // Force refresh profile after successful update to ensure latest data
        await fetchProfile();
      }
    };

    const handleProfileRefreshRequest = async () => {
      if (!fetchInProgressRef.current && user) {
        // Force refresh profile when requested
        await fetchProfile();
      }
    };

    const handleUserLogout = () => {
      // Immediately clear all profile data when user logs out
      setProfile(null);
      setProfileStrength(0);
      setError(null);
      setShouldShowCompletionDialog(false);
      setShouldShowUserTypeDialog(false);
      
      // Clear session storage
      sessionStorage.removeItem('profile_data');
      sessionStorage.removeItem('profile_strength');
      sessionStorage.removeItem('profile_cache_timestamp');
      
      // Reset refs
      fetchInProgressRef.current = false;
      lastUserIdRef.current = null;
    };

    window.addEventListener('auth-login-success', handleLoginSuccess);
    window.addEventListener('profile-updated', handleProfileUpdate);
    window.addEventListener('profile-refresh-requested', handleProfileRefreshRequest);
    window.addEventListener('user-logout', handleUserLogout);
    
    return () => {
      window.removeEventListener('auth-login-success', handleLoginSuccess);
      window.removeEventListener('profile-updated', handleProfileUpdate);
      window.removeEventListener('profile-refresh-requested', handleProfileRefreshRequest);
      window.removeEventListener('user-logout', handleUserLogout);
    };
  }, []);

  // Method to trigger profile refresh event (useful for external components)
  const triggerProfileRefresh = () => {
    window.dispatchEvent(new CustomEvent('profile-refresh-requested'));
  };

  const value: ProfileContextType = {
    profile,
    profileStrength,
    isLoading,
    error,
    fetchProfile,
    shouldShowCompletionDialog,
    setShouldShowCompletionDialog,
    dismissCompletionDialog,
    refreshProfile,
    updateProfile,
    triggerProfileRefresh,
    shouldShowUserTypeDialog,
    setShouldShowUserTypeDialog,
    updateProfileType,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfileContext() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfileContext must be used within a ProfileProvider');
  }
  return context;
}
